/**
 * Electron环境下的文件下载工具
 * 提供原生文件保存对话框和批量下载功能
 */

// 检查是否在Electron环境中
export const isElectron = (): boolean => {
  return !!(window as any).electronAPI || !!(window as any).require;
};

// 获取文件扩展名
export const getFileExtension = (url: string): string => {
  const match = url.match(/\.([^./?#]+)(?:[?#]|$)/i);
  return match ? match[1].toLowerCase() : '';
};

// 从URL获取文件名
export const getFileNameFromUrl = (url: string, fallbackName: string = 'download'): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split('/').pop();
    
    if (fileName && fileName.includes('.')) {
      return fileName;
    }
    
    // 如果没有文件名，使用扩展名
    const ext = getFileExtension(url);
    return ext ? `${fallbackName}.${ext}` : fallbackName;
  } catch {
    return fallbackName;
  }
};

// 下载单个文件（Electron环境）
export const downloadFileElectron = async (url: string, suggestedName: string): Promise<boolean> => {
  try {
    if (!isElectron()) {
      throw new Error('Not in Electron environment');
    }

    // 使用Electron的dialog API显示保存对话框
    const { ipcRenderer } = (window as any).require('electron');
    
    const result = await ipcRenderer.invoke('show-save-dialog', {
      title: '保存文件',
      defaultPath: suggestedName,
      filters: [
        { name: '所有文件', extensions: ['*'] },
        { name: '图片', extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'] },
        { name: '视频', extensions: ['mp4', 'webm', 'ogg', 'mov', 'avi'] },
        { name: '音频', extensions: ['mp3', 'wav', 'ogg', 'aac', 'm4a'] }
      ]
    });

    if (result.canceled || !result.filePath) {
      return false;
    }

    // 下载文件到指定路径
    const downloadResult = await ipcRenderer.invoke('download-file', {
      url: url,
      filePath: result.filePath
    });

    return downloadResult.success;
  } catch (error) {
    console.error('Electron下载失败:', error);
    return false;
  }
};

// 批量下载文件（Electron环境）
export const downloadMultipleFilesElectron = async (
  urls: string[], 
  taskName: string = 'download'
): Promise<{ success: number; total: number; errors: string[] }> => {
  try {
    if (!isElectron()) {
      throw new Error('Not in Electron environment');
    }

    const { ipcRenderer } = (window as any).require('electron');
    
    console.log(`🔄 准备批量下载 ${urls.length} 个文件`);
    
    // 显示文件夹选择对话框
    const result = await ipcRenderer.invoke('show-open-dialog', {
      title: `选择保存文件夹 - ${urls.length} 个文件`,
      properties: ['openDirectory', 'createDirectory'],
      message: `选择保存 ${urls.length} 个文件的文件夹`,
      buttonLabel: '选择文件夹'
    });

    console.log('📁 文件夹选择结果:', result);

    if (result.canceled || !result.filePaths || result.filePaths.length === 0) {
      console.log('❌ 用户取消了文件夹选择');
      return { success: 0, total: urls.length, errors: ['用户取消了操作'] };
    }

    const saveDirectory = result.filePaths[0];
    const results = { success: 0, total: urls.length, errors: [] as string[] };

    console.log(`📂 保存目录: ${saveDirectory}`);

    // 批量下载文件 - 使用Node.js path模块处理路径
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const fileName = getFileNameFromUrl(url, `${taskName}_${i + 1}`);
      
      // 使用IPC调用主进程的path.join来确保路径正确
      const { ipcRenderer: pathIpc } = (window as any).require('electron');
      const filePath = await pathIpc.invoke('join-path', saveDirectory, fileName);
      
      console.log(`📥 下载文件 ${i + 1}/${urls.length}: ${fileName}`);
      
      try {
        const downloadResult = await ipcRenderer.invoke('download-file', {
          url: url,
          filePath: filePath
        });

        if (downloadResult.success) {
          results.success++;
          console.log(`✅ 文件 ${i + 1} 下载成功: ${fileName}`);
        } else {
          const errorMsg = `文件 ${i + 1} (${fileName}): ${downloadResult.error || '下载失败'}`;
          results.errors.push(errorMsg);
          console.error(`❌ ${errorMsg}`);
        }
      } catch (error) {
        const errorMsg = `文件 ${i + 1} (${fileName}): ${error}`;
        results.errors.push(errorMsg);
        console.error(`❌ ${errorMsg}`);
      }
    }

    console.log(`📊 批量下载完成: ${results.success}/${results.total} 成功`);
    return results;
  } catch (error) {
    console.error('批量下载失败:', error);
    return { 
      success: 0, 
      total: urls.length, 
      errors: [`批量下载失败: ${error}`] 
    };
  }
};

// 传统浏览器下载（作为fallback）- 使用fetch+blob避免跨域跳转
export const downloadFileBrowser = async (url: string, fileName: string): Promise<boolean> => {
  try {
    // 使用fetch下载文件
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // 获取blob数据
    const blob = await response.blob();
    
    // 创建blob URL
    const blobUrl = window.URL.createObjectURL(blob);
    
    // 创建下载链接
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 释放blob URL
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    
    return true;
  } catch (error) {
    console.error('浏览器下载失败:', error);
    // 如果fetch失败，尝试直接使用a标签（可能会跳转）
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch {
      return false;
    }
  }
};

// 批量下载（浏览器环境）
export const downloadMultipleFilesBrowser = async (
  urls: string[], 
  taskName: string = 'download'
): Promise<{ success: number; total: number; errors: string[] }> => {
  const results = { success: 0, total: urls.length, errors: [] as string[] };

  for (let i = 0; i < urls.length; i++) {
    const url = urls[i];
    const fileName = getFileNameFromUrl(url, `${taskName}_${i + 1}`);
    
    try {
      const success = await downloadFileBrowser(url, fileName);
      if (success) {
        results.success++;
      } else {
        results.errors.push(`文件 ${i + 1} (${fileName}): 下载失败`);
      }
      
      // 延迟避免浏览器阻止
      if (i < urls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      results.errors.push(`文件 ${i + 1} (${fileName}): ${error}`);
    }
  }

  return results;
};

// 统一下载接口
export const downloadFile = async (url: string, suggestedName: string): Promise<boolean> => {
  if (isElectron()) {
    return await downloadFileElectron(url, suggestedName);
  } else {
    return await downloadFileBrowser(url, suggestedName);
  }
};

// 统一批量下载接口
export const downloadMultipleFiles = async (
  urls: string[], 
  taskName: string = 'download'
): Promise<{ success: number; total: number; errors: string[] }> => {
  if (isElectron()) {
    return await downloadMultipleFilesElectron(urls, taskName);
  } else {
    return await downloadMultipleFilesBrowser(urls, taskName);
  }
};
